import React, { FC, useContext } from 'react';
import { ascComparator, stringFormat, T } from 'src/shared/helpers';
import styled from 'styled-components';

import { MessageDialog, useDialogManager } from '../../../../../../widgets/dialog';
import { ConfirmDropdownItem, isConfirmedRun } from '../../confirmTwice';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import { DropdownActionItem, DropdownActions, DropdownItemIcon } from '../../../../../../widgets/dropdown';
import { GhostButton } from '../../../../../../widgets/buttons';
import { SearchInput } from '../../../../../../widgets/input';
import { useProductConfigurationState } from 'src/components/pages/productConfiguration/state';
import usePreview3DStore, { Preview3DStoreContext } from 'src/components/widgets/preview3D/store';
import AttributeIcon from 'src/shared/material_icons/image/tune.svg';
import GroupIcon from 'src/shared/material_icons/file/folder.svg';
import StepIcon from 'src/shared/material_icons/places/stairs.svg';
import AreaIcon from 'src/shared/material_icons/maps/category.svg';
import OptionIcon from 'src/shared/material_icons/image/image.svg';
import AIIcon from 'src/shared/material_icons/image/auto_awesome.svg';
import ImportIcon from 'src/shared/material_icons/content/content_copy.svg';
import PreSetIcon from 'src/shared/material_icons/content/inventory_2.svg';
import Plus from 'src/shared/material_icons/content/add.svg';
// SELECT ALL È LO STESSO SEGNO IN OGNI ELENCO (utente, 2026-10-21): quello della libreria Material.
import SelectAllIcon from 'src/shared/material_icons/content/select_all.svg';
import MoreIcon from 'src/shared/material_icons/navigation/more_vert.svg';
import DuplicateIcon from 'src/shared/material_icons/content/content_copy.svg';
import MergeIcon from 'src/shared/material_icons/maps/merge.svg';
import CopyToIcon from 'src/shared/material_icons/content/copy_all.svg';
import MoveToIcon from 'src/shared/material_icons/file/drive_file_move.svg';
import RemoveIcon from 'src/shared/material_icons/action/delete.svg';
import { CollapseAllIcon, ExpandAllIcon, FilterIcon, SingleBranchIcon } from '../../../../../../widgets/icons/scene';
import { ComposerAttributeTreeElement, ComposerRowAction } from '../../../configurator-composer/composer/components/composerAttributeTreeElement';
import { addComposerArea, addComposerOption } from './composerAddChild';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import ComposerGenerateAIAttributeDialog from '../../../configurator-composer/composer/dialogs/composerGenerateAIAttributeDialog';
import ComposerImportAttributeDialog from '../../../configurator-composer/composer/dialogs/composerImportAttributeDialog';
import ComposerRetargetAttributeDialog from '../../../configurator-composer/composer/dialogs/composerRetargetAttributeDialog';
import {
    getRetargetObjectsAndMaterials,
    randomCode,
    retargetMapToActions,
    sortByDisplay
} from '../../../configurator-composer/composer/helpers/composerHelpers';
import { ComposerAttributeNodeAdapter, ComposerTemplateNodeAdapter } from '../../../configurator-composer/composer/helpers/composerNodesAdapter';
import { keyText } from '../../keyText';
import { useStructureMoveCopy, useStructureSelectionCommands } from './structureSelectionCommands';
import { useOptionMoveCommand, useOptionSelectionCommands } from './optionSelectionCommands';
import { ComposerPickerEntry, composerRowMenuAnchor, useComposerPicker } from '../composerPickerMenu';
import ComposerOptionRows from './composerOptionRows';
import { blinkComposerRow, useComposerRevealListener } from '../composerReveal';
import { ComposerAreaRows, useAreaSelectionCommands } from '../customizableAreasEditor';
import { getComposerLeafIndent } from '../../../configurator-composer/composer/helpers/composerTreeIndent';
import { ComposerAttributeDropPosition, moveComposerAttributeNode } from '../../../configurator-composer/composer/helpers/composerTreeDnD';
import { carriedFrom, ReorderPosition, startPointerReorder } from '../../pointerReorder';
import { useRememberedBranches } from '../../treeBranchMemory';
import type { TreeNodeHook } from '../../tree';
import {
    ComposerAttributeNode,
    ComposerAttributeOptionNode,
    ComposerAttributePreviewType,
    ComposerAttributeType,
    ComposerAreaNode,
    isComposerAreaLinkedToAttribute
} from './composerAttributesModels';
import {
    COMPOSER_AI_TONE,
    composerKindTone,
    ComposerIconButton,
    ComposerList,
    ComposerAddButton,
    ComposerEmptyState,
    ComposerPanel,
    ComposerScrollBody,
    ComposerSearchRow,
    ComposerSelectionFooter,
    ComposerToolbar,
    ComposerToolbarDivider,
    ComposerToolbarSpacer
} from '../composerWorkspace';
import { useComposerTreeKeyboard } from './composerTreeKeyboard';

// LE RIGHE DELL'ALBERO STANNO UNA SOTTO L'ALTRA, come nell'Outliner della scena (utente, 2026-10-15):
// trentaquattro pixel di passo, senza aria in più fra una riga e l'altra.
const TreeList = styled(ComposerList)`
    gap: 0;
`;

// LA RICERCA HA ACCANTO IL FILTRO, sulla stessa riga (prototipo, riga 2 della testata).
const SearchRow = styled(ComposerSearchRow)`
    display: flex;
    align-items: center;
    gap: 6px;

    > :last-child {
        min-width: 0;
        flex: 1;
    }
`;

// IL FILTRO STA AL BORDO, sotto «Add» (utente, 2026-10-15): il tasto piccolo ha una larghezza minima e
// centrava l'icona in uno spazio vuoto; qui è largo quanto icona e parola, e l'icona parte dove parte Add.
const FilterButton = styled(GhostButton).attrs({ $size: 'small' })<{ $active: boolean }>`
    flex: none;
    gap: 4px;
    width: auto;
    min-width: 0;
    /* Alto quanto la ricerca accanto (26px, come nel prototipo): due altezze sulla stessa riga non si allineano. */
    height: 26px;
    min-height: 26px;
    margin-left: -4px;
    padding: 0 6px;
    color: ${(props) => (props.$active ? 'var(--editor-accent, #a94700)' : 'var(--editor-muted, #55595f)')};
    background: ${(props) => (props.$active ? 'var(--editor-accent-wash, #fdf0e6)' : 'transparent')};
    border-radius: 8px;
    font-size: 12px;
    white-space: nowrap;

    > svg {
        width: 16px;
        height: 16px;
    }
`;

type StructureTypeFilter = 'all' | 'attributes' | 'areas' | 'options';
type StructureStateFilter = 'hidden' | 'links';

// IL CONTENITORE DELLE RIGHE È ANCHE IL CAMPO DEL TRASCINAMENTO (`data-reorder-scope`): struttura,
// opzioni e aree stanno nello stesso elenco, e le regole dicono chi può cadere dove.
// L'Add dentro la frase dell'elenco vuoto: un bottone fantasma piccolo, come nel prototipo (btn sm ghost).
const EmptyAddButton = styled(GhostButton).attrs({ $size: 'small' })`
    display: inline-flex;
    vertical-align: baseline;
    color: var(--editor-accent, #a94700);
`;

const TreeDropSurface = styled(ComposerScrollBody)`
    position: relative;
`;

type ComposerAddKey = 'group' | 'step' | 'attribute' | 'personalisation' | 'preset';

const ComposerAttributes: FC<{ state: TreeNodeHook<ComposerAttributeNode> }> = ({ state }) => {
    const [
        currentAttributes,
        areas,
        selectedAreas,
        storeMeshList,
        storeMaterialsList,
        selectAttributes,
        updateAttribute,
        removeAttributes,
        cloneAttributes,
        updateArea,
        validateAttributes,
        selectOptions,
        selectAreas,
        setAttributeFace
    ] = useComposerStore((state) => [
        state.attributes,
        state.areas,
        state.selectedAreas,
        state.sceneObjectList,
        state.sceneMaterialList,
        state.selectAttributes,
        state.updateAttribute,
        state.removeAttributes,
        state.cloneAttributes,
        state.updateArea,
        state.validateAttributes,
        state.selectOptions,
        state.selectAreas,
        state.setAttributeFace
    ]);
    const [sceneEditor] = usePreview3DStore((x) => [x.sceneEditor]);

    const preview3DStore = useContext(Preview3DStoreContext);
    const { state: productState } = useProductConfigurationState();
    const { showDialog, closeDialog } = useDialogManager();

    const { filter, setFilter, selectedIds, filteredData, updateNode, findNodeById, removeNodes, treeState, setSelectedIds } = state;
    const treeKeyboard = useComposerTreeKeyboard();
    const highlightedAreaMeshes = React.useRef<BABYLON.AbstractMesh[]>([]);

    React.useEffect(() => {
        highlightedAreaMeshes.current.forEach((mesh) => sceneEditor?.highlightMesh?.(mesh, false));
        highlightedAreaMeshes.current = [];
        const selectedArea = selectedAreas?.[0];
        if (!selectedArea || !sceneEditor) return;
        const objectIDs = new Set(selectedArea.objects.split(';').filter(Boolean));
        const meshes = storeMeshList.filter((mesh) => objectIDs.has(mesh.id));
        sceneEditor.showHideHighlight?.(true);
        meshes.forEach((mesh) => sceneEditor.highlightMesh?.(mesh, true));
        highlightedAreaMeshes.current = meshes;
        if (selectedArea.camera) sceneEditor.setCameraLocation?.(selectedArea.camera);
    }, [sceneEditor, selectedAreas, storeMeshList]);

    const storeSelectedAttributes = useComposerStore((state) => state.selectedAttributes);
    React.useEffect(() => {
        if (storeSelectedAttributes && storeSelectedAttributes.length > 0) {
            const attributeIds = storeSelectedAttributes.map((attr) => attr.id);
            const currentIds = [...selectedIds].sort().join(',');
            const storeIds = [...attributeIds].sort().join(',');

            if (currentIds !== storeIds) {
                // Use queueMicrotask to ensure this happens after tree reinit
                queueMicrotask(() => {
                    setSelectedIds(attributeIds);
                });
            }
        } else if (selectedIds.length > 0 && (!storeSelectedAttributes || storeSelectedAttributes.length === 0)) {
            // Store has no selection, clear local selection
            setSelectedIds([]);
        }
    }, [storeSelectedAttributes, currentAttributes, selectedIds]); // Re-sync when attributes change (tree reinit)

    const selectedAttributes = React.useMemo(() => {
        if (!selectedIds || selectedIds.length === 0) return [];
        return selectedIds.map((id) => findNodeById(id)).filter((x) => x) as ComposerAttributeNode[];
    }, [selectedIds, findNodeById]);
    const sceneMeshList = storeMeshList.filter((x) => sceneEditor!.isRealMesh(x)).sort(ascComparator((item) => item.name ?? '')) ?? [];
    const sceneMaterialList =
        storeMaterialsList.filter((x) => x.id != 'default material' && x.name != 'default material').sort(ascComparator((item) => item.name ?? '')) ?? [];
    // IL «+» DI UN GRUPPO O DI UN PASSO apre la galleria Add per quel gruppo: finché la galleria è aperta, ciò
    // che nasce va lì e non nel gruppo della selezione di prima (la selezione dei callback è quella del clic).
    const homeGroupOverride = React.useRef<ComposerAttributeNode | null>(null);
    const getHomeGroup = () => {
        if (homeGroupOverride.current) return homeGroupOverride.current;
        let current: ComposerAttributeNode | undefined = selectedAttributes[0];
        while (current) {
            if (current.type === ComposerAttributeType.AttributeGroup) return current;
            const parentID: string | undefined = current.parentID;
            current = parentID ? currentAttributes.find((attribute) => attribute.id === parentID) : undefined;
        }
        return null;
    };

    const createAttributeNode = (name: string, code: string, previewType = ComposerAttributePreviewType.Rectangle) => {
        const homeGroup = getHomeGroup();
        const attribute = ComposerAttributeNodeAdapter.toNode(ComposerAttributeNodeAdapter.createNew());
        attribute.name = name;
        attribute.code = code;
        attribute.previewType = previewType;
        attribute.isNew = true;
        attribute.parentID = homeGroup?.id;
        attribute.parentIDNumeric = homeGroup?.idNumeric ?? -1;
        attribute.parentType = homeGroup?.type;
        attribute.camera = homeGroup?.camera;
        attribute.displayOrder =
            Math.max(-1, ...currentAttributes.filter((candidate) => candidate.parentID === homeGroup?.id).map((candidate) => candidate.displayOrder)) + 1;
        return attribute;
    };

    const createAreaNode = (attribute: ComposerAttributeNode, name: string): ComposerAreaNode => {
        const { side, modelSides } = ComposerTemplateNodeAdapter.fromAreaNode(null);
        const firstMesh = sceneMeshList[0];
        const area = ComposerTemplateNodeAdapter.toNodeArea(side, modelSides);
        area.name = name;
        area.attributeID = attribute.idNumeric;
        area.attributeGuid = attribute.id;
        area.displayOrder = areas.filter((candidate) => isComposerAreaLinkedToAttribute(candidate, attribute)).length;
        area.width = 10;
        area.height = 10;
        area.ppcm = 102.4;
        area.camera = attribute.camera || '';
        area.objects = firstMesh?.id || '';
        const hydrated = ComposerTemplateNodeAdapter.fromAreaNode(area);
        return {
            ...ComposerTemplateNodeAdapter.toNodeArea(hydrated.side, hydrated.modelSides),
            name,
            attributeID: attribute.idNumeric,
            attributeGuid: attribute.id,
            displayOrder: area.displayOrder,
            width: 10,
            height: 10,
            ppcm: 102.4,
            camera: area.camera,
            objects: area.objects
        };
    };

    const createAreaAttribute = (name: string, areaName: string) => {
        let code: string;
        do {
            code = randomCode('personalisation');
        } while (useComposerStore.getState().attributes.some((candidate) => candidate.code.trim().toLowerCase() === code.toLowerCase()));
        const attribute = createAttributeNode(name, code);
        attribute.attributeType = MPlaza.AttributeType.CustomizableArea;
        attribute.options = [];
        const area = createAreaNode(attribute, areaName);
        updateArea(area);
        landOnAttribute(attribute, 'areas');
        selectAreas([area]);
        return attribute;
    };

    const landOnAttribute = (attribute: ComposerAttributeNode, face: 'areas' | 'settings' | 'links' = 'settings') => {
        updateAttribute(attribute);
        validateAttributes();
        setSelectedIds([attribute.id]);
        selectAttributes([attribute]);
        selectOptions(null);
        selectAreas(null);
        setAttributeFace(face);
    };

    const buildStructure = (key: ComposerAddKey) => {
        if (key === 'preset') {
            const preset = ComposerAttributeNodeAdapter.toPresetNode(new MPlaza.TemplateGroup());
            const homeGroup = getHomeGroup();
            preset.isNew = true;
            preset.parentID = homeGroup?.id;
            preset.parentIDNumeric = homeGroup?.idNumeric ?? -1;
            preset.parentType = homeGroup?.type;
            preset.displayOrder = Math.max(-1, ...currentAttributes.filter((item) => item.parentID === homeGroup?.id).map((item) => item.displayOrder)) + 1;
            landOnAttribute(preset);
            return;
        }
        if (key === 'attribute') {
            landOnAttribute(createAttributeNode(T._('New attribute', 'Admin'), randomCode('attribute')), 'settings');
            return;
        }
        if (key === 'personalisation') {
            createAreaAttribute(T._('New personalisation', 'Admin'), T._('New area', 'Admin'));
            return;
        }
        if (key === 'group') {
            const group = ComposerAttributeNodeAdapter.toGroupNode(ComposerAttributeNodeAdapter.createNewGroup());
            group.name = T._('New group', 'Admin');
            group.isNew = true;
            group.displayOrder = Math.max(-1, ...currentAttributes.filter((attribute) => !attribute.parentID).map((attribute) => attribute.displayOrder)) + 1;
            landOnAttribute(group, 'settings');
            return;
        }
        if (key === 'step') {
            // Un passo sta in un gruppo, non in un altro passo: dal «+» di un passo nasce accanto a lui.
            const home = getHomeGroup();
            const homeGroup =
                home && home.type !== ComposerAttributeType.AttributeGroup
                    ? (currentAttributes.find((candidate) => candidate.id === home.parentID) ?? null)
                    : home;
            const step = ComposerAttributeNodeAdapter.toStepNode(ComposerAttributeNodeAdapter.createNewStep());
            step.name = T._('New step', 'Admin');
            step.isNew = true;
            step.parentID = homeGroup?.id;
            step.parentIDNumeric = homeGroup?.idNumeric ?? -1;
            step.parentType = homeGroup?.type;
            step.displayOrder =
                Math.max(-1, ...currentAttributes.filter((attribute) => attribute.parentID === homeGroup?.id).map((attribute) => attribute.displayOrder)) + 1;
            landOnAttribute(step, 'settings');
        }
    };

    // «ADD» È UN MENU, NON UNA FINESTRA (utente, 2026-10-15; prototipo V2). La galleria di prima proponeva casi
    // d'uso già pronti — colore, versione, incisione… —; ora le partenze stanno nell'attributo vuoto (i preset),
    // e qui resta che cosa si aggiunge, nell'ordine dell'albero: la struttura, ciò che il cliente cambia, ciò su
    // cui stampa — con i colori dei loro badge. «Generate with AI» sta fra ciò che il cliente cambia: crea
    // attributi e opzioni, non gruppi (utente). In fondo, importare da un altro prodotto e i preset.
    //
    // OPZIONE E AREA SONO VOCI FIGLIE (utente, 2026-10-16): rientrate sotto Attribute e Customizable area,
    // aggiungono dentro ciò che è scelto, e senza una scelta adatta restano spente dicendo perché.
    //
    // DAL «+» DI UNA RIGA SI OFFRE SOLO CIÒ CHE PUÒ NASCERE LÌ DENTRO (utente, 2026-10-16). In un gruppo: un passo,
    // un attributo, un'area, i preset — non un gruppo, che non si annida, né ciò che nasce sempre in cima (l'AI,
    // la copia da un altro prodotto). In un passo: un attributo o un'area.
    const { openPicker: openAddPicker, picker: addPicker } = useComposerPicker();
    const openAddMenu = (anchor: HTMLElement | null | undefined, inside?: ComposerAttributeNode) => {
        // Il «+» di una riga dice dove nasce la cosa; Add in barra torna alla regola di sempre (la selezione).
        homeGroupOverride.current = inside ?? null;
        const done = () => {
            homeGroupOverride.current = null;
        };
        const pick = (run: () => void) => () => {
            run();
            done();
        };
        const store = useComposerStore.getState();
        const chosen =
            store.selectedAttributes?.[0] ??
            store.attributes.find((attribute) => attribute.options.some((option) => option.id === store.selectedOptions?.[0]?.id));
        const optionTarget = chosen && chosen.type === ComposerAttributeType.Attribute && !isAreaAttribute(chosen) ? chosen : undefined;
        const areaTarget = chosen && isAreaAttribute(chosen) ? chosen : undefined;
        const place = !inside ? 'toolbar' : inside.type === ComposerAttributeType.AttributeStep ? 'step' : 'group';

        type AddEntry = ComposerPickerEntry & { block: string; where: Array<'toolbar' | 'group' | 'step'> };
        const all: AddEntry[] = [
            {
                block: T._('Structure', 'Admin'),
                where: ['toolbar'],
                id: 'group',
                tone: composerKindTone('group'),
                label: T._('Group', 'Admin'),
                caption: T._('A folder for attributes and customizable areas', 'Admin'),
                icon: <GroupIcon />,
                onPick: pick(() => buildStructure('group'))
            },
            {
                block: T._('Structure', 'Admin'),
                where: ['toolbar', 'group'],
                id: 'step',
                tone: composerKindTone('group'),
                label: T._('Step', 'Admin'),
                caption: T._('A stage of the configurator, inside a group', 'Admin'),
                icon: <StepIcon />,
                onPick: pick(() => buildStructure('step'))
            },
            {
                block: T._('Configure', 'Admin'),
                where: ['toolbar', 'group', 'step'],
                id: 'attribute',
                tone: composerKindTone('attribute'),
                label: T._('Attribute', 'Admin'),
                caption: T._('Something the buyer can change', 'Admin'),
                icon: <AttributeIcon />,
                onPick: pick(() => buildStructure('attribute'))
            },
            {
                block: T._('Configure', 'Admin'),
                where: ['toolbar'],
                child: true,
                id: 'option',
                tone: composerKindTone('option'),
                label: T._('Option', 'Admin'),
                caption: optionTarget ? stringFormat(T._('One option inside {0}', 'Admin'), optionTarget.name) : T._('Select an attribute first', 'Admin'),
                icon: <OptionIcon />,
                disabled: !optionTarget,
                onPick: pick(() => {
                    if (!optionTarget) return;
                    setOpenAttributeIds((previous) => new Set([...previous, optionTarget.id]));
                    addComposerOption(optionTarget.id);
                })
            },
            {
                block: T._('Configure', 'Admin'),
                where: ['toolbar'],
                id: 'ai',
                tone: COMPOSER_AI_TONE,
                accent: true,
                label: T._('Generate with AI', 'Admin'),
                caption: T._('Attributes and options from a description', 'Admin'),
                icon: <AIIcon />,
                onPick: pick(() => onAttributeButtonClick('add-ai'))
            },
            {
                block: T._('Personalize', 'Admin'),
                where: ['toolbar', 'group', 'step'],
                id: 'customizable-area',
                tone: composerKindTone('attribute'),
                label: T._('Customizable area', 'Admin'),
                caption: T._('A spot the buyer can print on', 'Admin'),
                icon: <AreaIcon />,
                onPick: pick(() => buildStructure('personalisation'))
            },
            {
                block: T._('Personalize', 'Admin'),
                where: ['toolbar'],
                child: true,
                id: 'area',
                tone: composerKindTone('option'),
                label: T._('Area', 'Admin'),
                caption: areaTarget
                    ? stringFormat(T._('One printable area inside {0}', 'Admin'), areaTarget.name)
                    : T._('Select a customizable area first', 'Admin'),
                icon: <AreaIcon />,
                disabled: !areaTarget,
                onPick: pick(() => {
                    if (!areaTarget) return;
                    setOpenAttributeIds((previous) => new Set([...previous, areaTarget.id]));
                    addComposerArea(areaTarget.id, (mesh) => !!sceneEditor?.isRealMesh(mesh));
                })
            },
            {
                block: T._('Import', 'Admin'),
                where: ['toolbar'],
                id: 'import',
                tone: composerKindTone('neutral'),
                label: T._('Copy from another product', 'Admin'),
                caption: T._('Attributes and options of a product you already have', 'Admin'),
                icon: <ImportIcon />,
                onPick: pick(() => onAttributeButtonClick('import'))
            },
            {
                block: T._('Import', 'Admin'),
                where: ['toolbar', 'group'],
                id: 'preset',
                tone: composerKindTone('neutral'),
                label: T._('Pre-set configurations', 'Admin'),
                caption: T._('Ready-made combinations the buyer can pick', 'Admin'),
                icon: <PreSetIcon />,
                onPick: pick(() => buildStructure('preset'))
            }
        ];
        // Il titolo della sezione sta sulla prima voce rimasta di ogni blocco.
        const entries = all
            .filter((entry) => entry.where.includes(place))
            .map(({ block, where: _where, ...entry }, index, list) => ({
                ...entry,
                section: index === 0 || list[index - 1].block !== block ? block : undefined
            }));
        openAddPicker(anchor, entries, undefined, true);
    };

    const onAttributeButtonClick = (action: string) => {
        switch (action) {
            case 'add':
                openAddMenu(document.querySelector<HTMLElement>('[data-composer-add]'));
                break;
            case 'add-ai':
                showDialog(
                    'composerGenerateAIAttribute',
                    <Preview3DStoreContext.Provider value={preview3DStore}>
                        <ComposerGenerateAIAttributeDialog
                            onClose={() => closeDialog('composerGenerateAIAttribute')}
                            materialList={sceneMaterialList}
                            meshList={sceneMeshList}
                            currentAttributes={currentAttributes}
                            sceneEditor={preview3DStore?.getState().sceneEditor}
                            onConfirm={(attributes) => {
                                closeDialog('composerGenerateAIAttribute');
                                if (!attributes) return;
                                attributes.forEach((attribute) => {
                                    attribute.isNew = true;
                                    updateAttribute(attribute);
                                });
                            }}
                        />
                    </Preview3DStoreContext.Provider>
                );
                break;
            case 'remove':
                //confirm dialog
                if (!selectedAttributes || selectedAttributes.length === 0) {
                    console.warn('No attribute selected for removal');
                    return;
                }
                showDialog(
                    'confirmDialog',
                    <MessageDialog
                        title={T._('Confirm Removal', 'Admin')}
                        message={
                            T._('Are you sure you want to remove the selected attribute(s)? ', 'Admin') + selectedAttributes.map((attr) => attr.name).join(', ')
                        }
                        buttons={[
                            {
                                label: T._('Remove', 'Admin'),
                                destructive: true,
                                onClick: () => {
                                    closeDialog('confirmDialog');
                                    const ids = selectedAttributes.map((attr) => attr.id);
                                    const childIds = selectedAttributes.flatMap((attr) => getNodeDescendants(attr)).map((child) => child.id);
                                    removeNodes(childIds);
                                    removeAttributes(childIds);
                                    removeNodes(ids);
                                    removeAttributes(ids);
                                    validateAttributes();
                                    setSelectedIds([]);
                                    selectAttributes(null);
                                }
                            }
                        ]}
                    />
                );
                break;
            case 'duplicate':
                if (!selectedAttributes || selectedAttributes.length === 0) {
                    console.warn('No attribute selected for duplication');
                    return;
                }
                const attributesToClone = [...selectedAttributes];
                const descendantsToClone: ComposerAttributeNode[] = [];

                const findDescendants = (node: ComposerAttributeNode) => {
                    const children = getNodeChildren(node);
                    for (const child of children) {
                        descendantsToClone.push(child);
                        findDescendants(child);
                    }
                };

                selectedAttributes.forEach(findDescendants);

                const allToClone = [...descendantsToClone, ...attributesToClone];
                const clonedAttributes = cloneAttributes(allToClone);
                validateAttributes();
                const clonedSelection = clonedAttributes.slice(Math.max(0, clonedAttributes.length - attributesToClone.length));
                setSelectedIds(clonedSelection.map((attribute) => attribute.id));
                selectAttributes(clonedSelection);
                selectOptions(null);
                break;
            case 'import':
                showDialog(
                    'composerImportAttribute',
                    <ComposerImportAttributeDialog
                        salesChannelID={productState.salesChannelID}
                        materialsList={sceneMaterialList}
                        objectsList={sceneMeshList}
                        onConfirm={(result) => {
                            closeDialog('composerImportAttribute');
                            if (!result || !result.selectedAttributes || result.selectedAttributes.length === 0) {
                                return;
                            }
                            const imported: ComposerAttributeNode[] = [];
                            for (const srcAttr of result.selectedAttributes) {
                                // Clone the attribute with new IDs
                                const newAttr: ComposerAttributeNode = {
                                    ...srcAttr,
                                    id: MPlaza.generateUUID(),
                                    idNumeric: -1,
                                    code: randomCode(srcAttr.code || 'attribute'),
                                    isNew: true,
                                    displayOrder: -1,
                                    parentID: undefined,
                                    parentIDNumeric: -1,
                                    parentType: undefined,
                                    links: [],
                                    linkOperation: undefined,
                                    options: srcAttr.options.map((opt, idx) => {
                                        const clonedOpt = {
                                            ...opt,
                                            id: MPlaza.generateUUID(),
                                            idNumeric: -1,
                                            attributeID: '', // Will be set below
                                            links: [],
                                            linkOperation: undefined,
                                            displayOrder: idx,
                                            actions: (opt.actions || []).map((action) => ({
                                                ...action,
                                                id: MPlaza.generateUUID(),
                                                attributeID: '',
                                                optionID: ''
                                            }))
                                        };
                                        return clonedOpt;
                                    })
                                };

                                // Fix references after generating the new attribute ID
                                newAttr.options = newAttr.options.map((opt) => ({
                                    ...opt,
                                    attributeID: newAttr.id,
                                    actions: opt.actions.map((a) => ({
                                        ...a,
                                        attributeID: newAttr.id,
                                        optionID: opt.id
                                    }))
                                }));

                                // Remap actions using object/material matches
                                const allActions = newAttr.options.flatMap((opt) => opt.actions || []);
                                const remapped = retargetMapToActions(allActions, result.materialMatches, result.objectMatches);
                                newAttr.options.forEach((opt) => {
                                    opt.actions = (opt.actions || []).map((a) => {
                                        const found = remapped.find((r) => r.id === a.id);
                                        return found || a;
                                    });
                                });

                                updateAttribute(newAttr);
                                imported.push(newAttr);
                            }
                            validateAttributes();
                            setSelectedIds(imported.map((a) => a.id));
                            selectAttributes(imported);
                        }}
                    />
                );
                break;
            case 'retarget':
                const currentAttr = selectedAttributes?.[0] || null;
                if (!currentAttr) {
                    console.warn('Retargeting attributes is only available for a single selected attribute');
                    return;
                }
                const options = currentAttr.options || [];
                if (options.length === 0) {
                    console.warn('Selected attribute has no options to retarget');
                    return;
                }
                const actions = options.flatMap((opt) => opt.actions || []);
                const { objectIds, materialIds } = getRetargetObjectsAndMaterials(actions);
                showDialog(
                    'composerRetargetAttribute',
                    <ComposerRetargetAttributeDialog
                        materialsList={sceneMaterialList}
                        objectsList={sceneMeshList}
                        usedMaterialsList={sceneMaterialList.filter((m) => materialIds.includes(m.id))}
                        usedObjectsList={sceneMeshList.filter((o) => objectIds.includes(o.id))}
                        onConfirm={(data) => {
                            if (!data) {
                                closeDialog('composerRetargetAttribute');
                                return;
                            }
                            closeDialog('composerRetargetAttribute');
                            const { materialMap, objectMap } = data;
                            retargetMapToActions(actions, materialMap, objectMap).forEach((action) => {
                                options.forEach((opt) => {
                                    const actionIndex = opt.actions?.findIndex((a) => a.id === action.id) ?? -1;
                                    if (actionIndex >= 0) {
                                        opt.actions![actionIndex] = action;
                                    }
                                });
                            });
                            currentAttr.options = options;
                            updateAttribute(currentAttr);
                        }}
                    />
                );
                break;
            default:
                console.warn('Unknown action:', action);
        }
    };
    // CERCARE IN UN ALBERO NON È FILTRARE UN ELENCO: una riga che corrisponde va mostrata con i
    // rami che la reggono, o non ha dove comparire — e un gruppo che corrisponde va mostrato con
    // ciò che contiene, o si apre sul vuoto. `filteredData` tiene le sole righe che corrispondono,
    // e con una struttura a due livelli questo voleva dire non trovare mai niente dentro un gruppo.
    const searchText = filter.trim().toLowerCase();
    // IL FILTRO PER TIPO E STATO (utente, 2026-10-15; prototipo «All ▾»), con i nomi del Backoffice: Attributes,
    // Customizable areas, Options; e fra gli stati le righe spente e quelle con dei Links. Si somma alla ricerca:
    // restano le righe che rispondono a tutte e due, con i loro gruppi sopra, e l'albero si apre tutto come quando
    // si cerca.
    const [typeFilter, setTypeFilter] = React.useState<StructureTypeFilter>('all');
    const [stateFilter, setStateFilter] = React.useState<Set<StructureStateFilter>>(() => new Set());
    const filterActive = typeFilter !== 'all' || stateFilter.size > 0;
    const matchesState = (row: { enabled?: boolean; links?: unknown[] }) =>
        (!stateFilter.has('hidden') || row.enabled === false) && (!stateFilter.has('links') || (row.links?.length ?? 0) > 0);
    // Le righe foglia che restano sotto un attributo: con «Attributes» nessuna, con uno stato quelle che ce l'hanno.
    const optionVisible = (option: ComposerAttributeOptionNode) => !filterActive || (typeFilter !== 'attributes' && matchesState(option));
    const visibleData = React.useMemo(() => {
        if (!searchText && !filterActive) return filteredData;
        const isArea = (attribute: ComposerAttributeNode) =>
            attribute.type === ComposerAttributeType.Attribute && attribute.attributeType === MPlaza.AttributeType.CustomizableArea;
        const isPlainAttribute = (attribute: ComposerAttributeNode) => attribute.type === ComposerAttributeType.Attribute && !isArea(attribute);
        const matchesFilter = (attribute: ComposerAttributeNode) => {
            if (typeFilter === 'attributes') return isPlainAttribute(attribute) && matchesState(attribute);
            if (typeFilter === 'areas') return isArea(attribute) && matchesState(attribute);
            if (typeFilter === 'options') return isPlainAttribute(attribute) && attribute.options.some(matchesState);
            return matchesState(attribute) || (isPlainAttribute(attribute) && attribute.options.some(matchesState));
        };
        const matchesSearch = (attribute: ComposerAttributeNode) =>
            !searchText ||
            (attribute.name ?? '').toLowerCase().includes(searchText) ||
            areas.some((area) => isComposerAreaLinkedToAttribute(area, attribute) && (area.name ?? '').toLowerCase().includes(searchText));

        const byId = new Map(currentAttributes.map((attribute) => [attribute.id, attribute]));
        const childrenOf = new Map<string, ComposerAttributeNode[]>();
        currentAttributes.forEach((attribute) => {
            if (!attribute.parentID) return;
            childrenOf.set(attribute.parentID, [...(childrenOf.get(attribute.parentID) ?? []), attribute]);
        });

        const keep = new Set<string>();
        const keepDescendants = (id: string) =>
            (childrenOf.get(id) ?? []).forEach((child) => {
                keep.add(child.id);
                keepDescendants(child.id);
            });

        currentAttributes
            .filter((attribute) => matchesSearch(attribute) && (!filterActive || matchesFilter(attribute)))
            .forEach((attribute) => {
                keep.add(attribute.id);
                // Un gruppo trovato per nome porta con sé ciò che contiene; col filtro no, perché il contenuto
                // risponde da sé.
                if (!filterActive) keepDescendants(attribute.id);
                let parentID = attribute.parentID;
                while (parentID) {
                    keep.add(parentID);
                    parentID = byId.get(parentID)?.parentID;
                }
            });

        return currentAttributes.filter((attribute) => keep.has(attribute.id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [areas, currentAttributes, filteredData, searchText, typeFilter, stateFilter]);

    const parentNodes = React.useMemo(() => visibleData.filter((a) => (a.isNew && !a.parentID) || !a.parentID), [visibleData]);
    const nodes = React.useMemo(() => parentNodes.sort((a, b) => sortByDisplay(parentNodes, a, b)), [parentNodes]);

    const getNodeChildren = (node: ComposerAttributeNode) => {
        return visibleData.filter((attr) => attr.parentID === node.id);
    };

    const getNodeDescendants = (node: ComposerAttributeNode): ComposerAttributeNode[] =>
        getNodeChildren(node).flatMap((child) => [child, ...getNodeDescendants(child)]);

    const selectSingleAttribute = (attribute: ComposerAttributeNode) => {
        setSelectedIds([attribute.id]);
        selectAttributes([attribute]);
        selectOptions(null);
        selectAreas(null);
        if (attribute.type === ComposerAttributeType.Attribute)
            setAttributeFace(attribute.attributeType === MPlaza.AttributeType.CustomizableArea ? 'areas' : 'options');
    };

    const removeAttributeRows = (attributesToRemove: ComposerAttributeNode[]) => {
        const run = () => {
            const descendants = attributesToRemove.flatMap((attribute) => getNodeDescendants(attribute));
            const ids = [...descendants, ...attributesToRemove].map((attribute) => attribute.id);
            removeNodes(ids);
            removeAttributes(ids);
            validateAttributes();
            setSelectedIds([]);
            selectAttributes(null);
            selectOptions(null);
        };
        // Dal menu si chiede due volte, senza finestra (confirmTwice): arrivati qui, la domanda è già fatta.
        if (isConfirmedRun()) return run();
        showDialog(
            'confirmDialog',
            <MessageDialog
                title={T._('Confirm Removal', 'Admin')}
                message={
                    T._('Are you sure you want to remove the selected item(s)? ', 'Admin') + attributesToRemove.map((attribute) => attribute.name).join(', ')
                }
                buttons={[
                    {
                        label: T._('Remove', 'Admin'),
                        destructive: true,
                        onClick: () => {
                            closeDialog('confirmDialog');
                            run();
                        }
                    }
                ]}
            />
        );
    };

    const retargetAttributeRow = (attribute: ComposerAttributeNode) => {
        const options = attribute.options || [];
        if (options.length === 0) return;

        const actions = options.flatMap((option) => option.actions || []);
        const { objectIds, materialIds } = getRetargetObjectsAndMaterials(actions);
        showDialog(
            'composerRetargetAttribute',
            <ComposerRetargetAttributeDialog
                materialsList={sceneMaterialList}
                objectsList={sceneMeshList}
                usedMaterialsList={sceneMaterialList.filter((material) => materialIds.includes(material.id))}
                usedObjectsList={sceneMeshList.filter((object) => objectIds.includes(object.id))}
                onConfirm={(data) => {
                    closeDialog('composerRetargetAttribute');
                    if (!data) return;

                    const { materialMap, objectMap } = data;
                    const remappedActions = retargetMapToActions(actions, materialMap, objectMap);
                    const updatedAttribute = {
                        ...attribute,
                        options: options.map((option) => ({
                            ...option,
                            actions: option.actions.map((action) => remappedActions.find((candidate) => candidate.id === action.id) ?? action)
                        }))
                    };
                    updateAttribute(updatedAttribute);
                    updateNode(updatedAttribute.id, updatedAttribute);
                    validateAttributes();
                    selectSingleAttribute(updatedAttribute);
                }}
            />
        );
    };

    // IL «…» DI UNA RIGA SCELTA INSIEME AD ALTRE VALE PER TUTTA LA SELEZIONE (round 4, pacchetto F), come
    // nello Scene Editor: gli stessi comandi del pannello della selezione multipla.
    const selectionCommands = useStructureSelectionCommands(storeSelectedAttributes ?? []);
    const { cloneOptions, removeOptions, openCopyPicker, openImagePicker, picker: imagePicker } = useOptionSelectionCommands();
    const { cloneAreas, removeAreas } = useAreaSelectionCommands();
    const { openMoveTo, openCopyTo, canMoveOrCopy, picker: structurePicker } = useStructureMoveCopy();
    const { openMoveOptions, picker: optionMovePicker } = useOptionMoveCommand();
    const selectionMenuRef = React.useRef<HTMLButtonElement>(null);
    const rowSelection =
        selectedIds.length > 1
            ? {
                  count: selectedIds.length,
                  canClone: selectionCommands.selectionRules.canDuplicate,
                  canRetarget: selectionCommands.selectionRules.canRetarget,
                  canGroup: selectionCommands.selectionRules.canGroup,
                  canMerge: selectionCommands.selectionRules.canMerge,
                  canImage: selectionCommands.selectionRules.canImage
              }
            : undefined;

    // IL «+» DELLA RIGA (utente, 2026-10-15): un'opzione o un'area nascono subito; dentro un gruppo o un passo
    // si apre la galleria Add, e ciò che si sceglie nasce lì.
    const addInside = (attribute: ComposerAttributeNode) => {
        if (attribute.type === ComposerAttributeType.Attribute) {
            setOpenAttributeIds((previous) => new Set([...previous, attribute.id]));
            return isAreaAttribute(attribute) ? addComposerArea(attribute.id, (mesh) => !!sceneEditor?.isRealMesh(mesh)) : addComposerOption(attribute.id);
        }
        selectSingleAttribute(attribute);
        openAddMenu(composerRowMenuAnchor(attribute.id), attribute);
    };

    const onAttributeRowAction = (action: ComposerRowAction | 'edit', attribute: ComposerAttributeNode) => {
        if (action === 'add') return addInside(attribute);
        // Group, Merge e l'immagine compaiono solo nel «…» di una riga scelta insieme ad altre: valgono per la selezione.
        if (action === 'group') return selectionCommands.groupSelection();
        if (action === 'merge') return selectionCommands.mergeSelection();
        if (action === 'image') return openImagePicker(composerRowMenuAnchor(attribute.id), selectionCommands.selectionOptions);
        // MOVE TO E COPY TO valgono per la riga, o per la selezione di cui fa parte.
        if (action === 'move' || action === 'copy') {
            const rows = rowSelection && selectedIds.includes(attribute.id) ? (storeSelectedAttributes ?? [attribute]) : [attribute];
            return (action === 'move' ? openMoveTo : openCopyTo)(composerRowMenuAnchor(attribute.id), rows);
        }
        if (rowSelection && selectedIds.includes(attribute.id)) {
            if (action === 'duplicate') return selectionCommands.duplicateSelection();
            if (action === 'retarget') return selectionCommands.retargetSelection();
            if (action === 'remove') return selectionCommands.removeSelection();
        }
        if (action === 'edit') {
            selectSingleAttribute(attribute);
            setAttributeFace('settings');
            return;
        }
        if (action === 'links') {
            selectSingleAttribute(attribute);
            setAttributeFace('links');
            return;
        }
        if (action === 'duplicate') {
            const descendants: ComposerAttributeNode[] = [];
            const collectDescendants = (current: ComposerAttributeNode) => {
                getNodeChildren(current).forEach((child) => {
                    descendants.push(child);
                    collectDescendants(child);
                });
            };
            collectDescendants(attribute);
            const cloned = cloneAttributes([...descendants, attribute]);
            validateAttributes();
            const clonedRow = cloned[cloned.length - 1];
            setSelectedIds(clonedRow ? [clonedRow.id] : []);
            selectAttributes(clonedRow ? [clonedRow] : null);
            selectOptions(null);
            return;
        }
        if (action === 'retarget') {
            selectSingleAttribute(attribute);
            retargetAttributeRow(attribute);
            return;
        }
        removeAttributeRows([attribute]);
    };
    const toggleAttributeEnabled = (attribute: ComposerAttributeNode) => {
        // Read the current store value at click time. Tree rows can lag one render behind the
        // validated Composer store, which previously made a second click write the same value
        // again and made the eye control appear inert.
        const currentAttribute = useComposerStore.getState().attributes.find((candidate) => candidate.id === attribute.id) ?? attribute;
        const updatedAttribute = { ...currentAttribute, enabled: currentAttribute.enabled === false };
        updateAttribute(updatedAttribute);
        const validatedAttribute = useComposerStore.getState().attributes.find((candidate) => candidate.id === updatedAttribute.id) ?? updatedAttribute;
        updateNode(validatedAttribute.id, validatedAttribute);
    };
    // IL NOME CAMBIATO DALL'ALBERO SEGUE LA STRADA DEL CAMPO IN TESTATA: stesso `isNew`, stessa
    // validazione — un nome doppio si accende di avviso qualunque sia il posto da cui è stato scritto.
    const renameAttribute = (attribute: ComposerAttributeNode, name: string) => {
        const currentAttribute = useComposerStore.getState().attributes.find((candidate) => candidate.id === attribute.id) ?? attribute;
        const updatedAttribute = { ...currentAttribute, name, isNew: true };
        updateAttribute(updatedAttribute);
        const validatedAttribute = useComposerStore.getState().attributes.find((candidate) => candidate.id === updatedAttribute.id) ?? updatedAttribute;
        updateNode(validatedAttribute.id, validatedAttribute);
        void validateAttributes();
    };
    // L'ULTIMA RIGA PREMUTA SENZA ⇧, attributo od opzione: da lì parte l'intervallo di un ⇧click.
    const rangeAnchorRef = React.useRef<string | null>(null);
    const getVisibleStructureRows = () => {
        const visible: ComposerAttributeNode[] = [];
        const visit = (node: ComposerAttributeNode) => {
            visible.push(node);
            if (node.type === ComposerAttributeType.Attribute || rowExpanded(node)) return;
            getNodeChildren(node)
                .sort((left, right) => sortByDisplay(filteredData, left, right))
                .forEach(visit);
        };
        nodes.forEach(visit);
        return visible;
    };

    const nodeSelectHandler = (event: React.MouseEvent, node: ComposerAttributeNode) => {
        const ctrlKey = event.ctrlKey || event.metaKey;
        const nodeFromStore = currentAttributes.find((attribute) => attribute.id === node.id);
        if (!nodeFromStore) return;
        if (!event.shiftKey) rangeAnchorRef.current = node.id;

        if (event.shiftKey && selectedIds.length > 0) {
            const visibleRows = getVisibleStructureRows();
            const anchor = visibleRows.findIndex((candidate) => candidate.id === selectedIds[selectedIds.length - 1]);
            const end = visibleRows.findIndex((candidate) => candidate.id === node.id);
            if (anchor < 0 || end < 0) return;
            const range = visibleRows.slice(Math.min(anchor, end), Math.max(anchor, end) + 1);
            const selectedRange = range
                .map((candidate) => currentAttributes.find((attribute) => attribute.id === candidate.id))
                .filter(Boolean) as ComposerAttributeNode[];
            setSelectedIds(selectedRange.map((attribute) => attribute.id));
            selectAttributes(selectedRange);
            selectOptions(null);
            selectAreas(null);
            return;
        }

        if (ctrlKey) {
            const nextSelection = selectedIds.includes(node.id)
                ? selectedAttributes.filter((attribute) => attribute.id !== node.id)
                : [...selectedAttributes, nodeFromStore];
            setSelectedIds(nextSelection.map((attribute) => attribute.id));
            selectAttributes(nextSelection);
            selectOptions(null);
            selectAreas(null);
            return;
        }

        setSelectedIds([node.id]);
        selectAttributes([nodeFromStore]);
        selectOptions(null);
        selectAreas(null);
        if (nodeFromStore.type === ComposerAttributeType.Attribute)
            setAttributeFace(nodeFromStore.attributeType === MPlaza.AttributeType.CustomizableArea ? 'areas' : 'options');
    };

    const moveNodeHandler = (node: ComposerAttributeNode, direction: 'up' | 'down') => {
        const siblings = filteredData
            .filter((n) => n.parentID === node.parentID)
            .sort((a, b) =>
                sortByDisplay(
                    filteredData.filter((n) => n.parentID === node.parentID),
                    a,
                    b
                )
            );

        const currentIndex = siblings.findIndex((n) => n.id === node.id);
        const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (swapIndex >= 0 && swapIndex < siblings.length) {
            const swapNode = siblings[swapIndex];
            const currentOrder = node.displayOrder;

            updateNode(node.id, { ...node, displayOrder: swapNode.displayOrder });
            updateNode(swapNode.id, { ...swapNode, displayOrder: currentOrder });
            updateAttribute({ ...node, displayOrder: swapNode.displayOrder });
            updateAttribute({ ...swapNode, displayOrder: currentOrder });
            validateAttributes();
        }
    };

    // IL TRASCINAMENTO DELLA STRUTTURA (pointerReorder; utente, 2026-10-15): gruppi, passi, attributi e
    // preset si spostano davanti, dopo o dentro un'altra riga con le regole di `moveComposerAttributeNode`.
    // Una riga scelta porta con sé tutta la selezione — ognuna col suo ramo —, nell'ordine dell'albero.
    const carriedStructure = (draggedId: string) => {
        const ordered = getVisibleStructureRows().map((node) => node.id);
        const carried = carriedFrom(ordered, draggedId, selectedIds);
        // Una riga dentro un'altra già portata viaggia col ramo: non si sposta due volte.
        return carried.filter((id) => !carried.some((other) => other !== id && pathOf(id).has(other)));
    };

    const moveStructure = (carriedIds: string[], targetId: string, position: ReorderPosition) => {
        const requested: ComposerAttributeDropPosition = position === 'into' ? 'inside' : position;
        // Davanti al bersaglio si entra nell'ordine; dopo, all'indietro, così il primo resta primo.
        const sequence = position === 'after' ? [...carriedIds].reverse() : carriedIds;
        return sequence.reduce((attributes, id) => moveComposerAttributeNode(attributes, id, targetId, requested), currentAttributes);
    };

    const startStructureDrag = (event: React.PointerEvent<HTMLElement>, node: ComposerAttributeNode) => {
        const carriedIds = carriedStructure(node.id);
        startPointerReorder(event, {
            fromRow: true,
            draggedId: node.id,
            carriedIds,
            canDrop: (targetId, position) => {
                if (carriedIds.includes(targetId) || !currentAttributes.some((attribute) => attribute.id === targetId)) return false;
                const requested: ComposerAttributeDropPosition = position === 'into' ? 'inside' : position;
                return carriedIds.every((id) => moveComposerAttributeNode(currentAttributes, id, targetId, requested) !== currentAttributes);
            },
            onDrop: (targetId, position) => {
                const updatedAttributes = moveStructure(carriedIds, targetId, position);
                if (updatedAttributes === currentAttributes) return;
                updatedAttributes.forEach((updatedNode, index) => {
                    const currentNode = currentAttributes[index];
                    if (
                        currentNode.parentID === updatedNode.parentID &&
                        currentNode.parentIDNumeric === updatedNode.parentIDNumeric &&
                        currentNode.parentType === updatedNode.parentType &&
                        currentNode.displayOrder === updatedNode.displayOrder
                    ) {
                        return;
                    }
                    updateNode(updatedNode.id, updatedNode);
                    updateAttribute(updatedNode);
                });
                // Lasciata dentro un ramo chiuso, la riga si vede: il ramo si apre.
                if (position === 'into') setOpenAttributeIds((previous) => new Set([...previous, targetId]));
                const moved = updatedAttributes.filter((attribute) => carriedIds.includes(attribute.id));
                setSelectedIds(moved.map((attribute) => attribute.id));
                selectAttributes(moved);
                selectOptions(null);
                selectAreas(null);
                void validateAttributes();
            }
        });
    };

    // Removed problematic useEffect that was causing "Rendered fewer hooks" error
    // This was auto-selecting first attribute but interfered with manual selection logic

    // QUALI RAMI SONO APERTI LO SA L'ALBERO, NON I NODI (round 4, pacchetto H; 2026-10-15). Gruppi, passi e
    // attributi stanno in un insieme solo, qui. Prima l'apertura di un gruppo era scritta sul nodo
    // (`expanded`), ma i nodi si ricostruiscono dallo store a ogni modifica e l'apertura andava persa:
    // la scelta di aprire solo il primo ramo spariva al primo aggiornamento. Un attributo si apre col suo
    // chevron e si apre da solo quando scegli lui o una delle sue righe.
    const [openAttributeIds, setOpenAttributeIds] = React.useState<Set<string>>(() => new Set());
    const selectedOptions = useComposerStore((store) => store.selectedOptions);
    const leafOwnerIds = new Set([
        ...(selectedOptions ?? []).map((option) => option.attributeID),
        ...(selectedAreas ?? []).map((area) => area.attributeGuid).filter((id): id is string => !!id)
    ]);
    const isAreaAttribute = (node: ComposerAttributeNode) =>
        node.type === ComposerAttributeType.Attribute && node.attributeType === MPlaza.AttributeType.CustomizableArea;
    const hasLeaves = (node: ComposerAttributeNode) =>
        node.type === ComposerAttributeType.Attribute &&
        (isAreaAttribute(node) ? areas.some((area) => isComposerAreaLinkedToAttribute(area, node)) : node.options.length > 0);

    // UN RAMO ALLA VOLTA (utente, 2026-10-15; prototipo `cSolo`): acceso, chiude tutto, e da lì aprire un
    // ramo chiude gli altri — resta aperta solo la sua strada. Spento di partenza (utente): si apre
    // liberamente come prima. Expand all, acceso, non ha senso: si spegne finché non c'è niente di aperto.
    const [singleBranch, setSingleBranch] = React.useState(false);
    const pathOf = (id: string) => {
        const path = new Set([id]);
        let parentID = currentAttributes.find((attribute) => attribute.id === id)?.parentID;
        while (parentID && !path.has(parentID)) {
            path.add(parentID);
            parentID = currentAttributes.find((attribute) => attribute.id === parentID)?.parentID;
        }
        return path;
    };
    // «DOVE STA?» DALL'INSPECTOR (utente, 2026-10-15): apre la strada della riga — i gruppi sopra e
    // l'attributo che la contiene —, toglie una ricerca che la nasconderebbe, poi la mostra e la fa lampeggiare.
    useComposerRevealListener((id) => {
        const state = useComposerStore.getState();
        const owner =
            state.attributes.find((attribute) => attribute.id === id) ??
            state.attributes.find((attribute) => attribute.options.some((option) => option.id === id)) ??
            state.attributes.find((attribute) => state.areas.some((area) => area.id === id && isComposerAreaLinkedToAttribute(area, attribute)));
        if (!owner) return;
        const path = pathOf(owner.id);
        if (singleBranch) closeBranchesOutside(path);
        // I gruppi sopra si aprono sempre; l'attributo solo se la riga da mostrare è una sua opzione o area.
        setOpenAttributeIds((previous) => new Set([...previous, ...[...path].filter((branch) => branch !== owner.id || owner.id !== id)]));
        setFilter('');
        blinkComposerRow(id);
    });
    const closeBranchesOutside = (keep: Set<string>) => setOpenAttributeIds((previous) => new Set([...previous].filter((id) => keep.has(id))));

    React.useEffect(() => {
        const chosen = [...(storeSelectedAttributes?.length === 1 ? [storeSelectedAttributes[0].id] : []), ...leafOwnerIds];
        if (chosen.every((id) => openAttributeIds.has(id))) return;
        if (singleBranch) closeBranchesOutside(new Set(chosen.flatMap((id) => [...pathOf(id)])));
        // Con la riga si apre anche la strada fino a lei: i gruppi che la contengono.
        setOpenAttributeIds((previous) => new Set([...previous, ...chosen.flatMap((id) => [...pathOf(id)])]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeSelectedAttributes, selectedOptions, selectedAreas]);

    const toggleAttributeOpen = (node: ComposerAttributeNode) =>
        setOpenAttributeIds((previous) => {
            const next = new Set(previous);
            if (next.has(node.id)) next.delete(node.id);
            else next.add(node.id);
            return next;
        });
    // `expanded` è letto al contrario dalla riga (aperta quando è falso): qui si traduce. Con una ricerca
    // l'albero è tutto aperto.
    const typeFilterLabels: Record<StructureTypeFilter, string> = {
        all: T._('All', 'Admin'),
        attributes: T._('Attributes', 'Admin'),
        areas: T._('Customizable areas', 'Admin'),
        options: T._('Options', 'Admin')
    };
    const toggleStateFilter = (key: StructureStateFilter) =>
        setStateFilter((previous) => {
            const next = new Set(previous);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    const clearSelection = () => {
        rangeAnchorRef.current = null;
        setSelectedIds([]);
        selectAttributes(null);
        selectOptions(null);
        selectAreas(null);
    };
    const rowExpanded = (node: ComposerAttributeNode) => (searchText || filterActive ? false : !openAttributeIds.has(node.id));
    // OGNI APERTURA PASSA DA QUI: un gruppo, un passo o un attributo, dalla freccetta o col doppio clic.
    const toggleBranch = (node: ComposerAttributeNode) => {
        if (singleBranch && rowExpanded(node)) closeBranchesOutside(pathOf(node.id));
        toggleAttributeOpen(node);
    };
    // LO ⇧CLICK SU UNA RIGA FOGLIA PRENDE L'INTERVALLO DELLE RIGHE CHE VEDI (utente, 2026-10-14), anche
    // oltre il suo attributo: dall'ultima riga premuta senza ⇧ — una foglia o un attributo — fino a lei.
    // Del tratto restano le righe della stessa specie della premuta: opzioni con opzioni, aree con aree
    // (2026-10-15), perché un attributo e le sue righe non si scelgono insieme.
    const linkedAreasOf = (node: ComposerAttributeNode) =>
        areas.filter((area) => isComposerAreaLinkedToAttribute(area, node)).sort((left, right) => left.displayOrder - right.displayOrder);
    // LE RIGHE CHE SI VEDONO, nell'ordine dell'albero: strutture e, sotto gli attributi aperti, opzioni e aree.
    const visibleTreeRows = () => {
        const state = useComposerStore.getState();
        const rows: { id: string; option?: ComposerAttributeOptionNode; area?: ComposerAreaNode }[] = [];
        getVisibleStructureRows().forEach((node) => {
            rows.push({ id: node.id });
            if (!hasLeaves(node) || rowExpanded(node)) return;
            if (isAreaAttribute(node)) return linkedAreasOf(node).forEach((area) => rows.push({ id: area.id, area }));
            const owner = state.attributes.find((candidate) => candidate.id === node.id) ?? node;
            [...owner.options]
                .filter(optionVisible)
                .sort((left, right) => sortByDisplay(owner.options, left, right))
                .forEach((option) => rows.push({ id: option.id, option }));
        });
        return rows;
    };
    const selectLeafRange = (target: { kind: 'option'; option: ComposerAttributeOptionNode } | { kind: 'area'; area: ComposerAreaNode }) => {
        const state = useComposerStore.getState();
        const rows = visibleTreeRows();
        const targetId = target.kind === 'option' ? target.option.id : target.area.id;
        const owner =
            target.kind === 'option'
                ? state.attributes.find((candidate) => candidate.id === target.option.attributeID)
                : state.attributes.find((candidate) => isComposerAreaLinkedToAttribute(target.area, candidate));
        if (!owner) return;
        const lastLeaf =
            target.kind === 'option' ? state.selectedOptions?.[state.selectedOptions.length - 1] : state.selectedAreas?.[state.selectedAreas.length - 1];
        const anchorId = rangeAnchorRef.current ?? lastLeaf?.id ?? selectedIds[selectedIds.length - 1];
        const start = rows.findIndex((row) => row.id === anchorId);
        const end = rows.findIndex((row) => row.id === targetId);
        const span = start < 0 || end < 0 ? rows.filter((row) => row.id === targetId) : rows.slice(Math.min(start, end), Math.max(start, end) + 1);
        if (start < 0) rangeAnchorRef.current = targetId;
        setSelectedIds([owner.id]);
        selectAttributes([owner]);
        if (target.kind === 'option') {
            selectOptions(span.flatMap((row) => (row.option ? [row.option] : [])));
            selectAreas(null);
            return;
        }
        selectOptions(null);
        selectAreas(span.flatMap((row) => (row.area ? [row.area] : [])));
        setAttributeFace('areas');
    };
    const attributeLeaves = (node: ComposerAttributeNode, level: number) => {
        if (!hasLeaves(node)) return undefined;
        const indent = getComposerLeafIndent(level);
        return isAreaAttribute(node) ? (
            <ComposerAreaRows
                attribute={node}
                indent={indent}
                level={level + 1}
                rangeAnchorRef={rangeAnchorRef}
                onRangeSelect={(area) => selectLeafRange({ kind: 'area', area })}
            />
        ) : (
            <ComposerOptionRows
                attribute={node}
                indent={indent}
                level={level + 1}
                visible={filterActive ? optionVisible : undefined}
                rangeAnchorRef={rangeAnchorRef}
                onRangeSelect={(option) => selectLeafRange({ kind: 'option', option })}
            />
        );
    };
    // LA SCIA (utente, 2026-10-15): le righe che contengono la selezione — i gruppi sopra un attributo
    // scelto, l'attributo di un'opzione o di un'area scelta e i suoi gruppi — restano arancio, senza filo.
    const trailIds = new Set<string>();
    const climb = (id: string | undefined) => {
        let parentID = currentAttributes.find((attribute) => attribute.id === id)?.parentID;
        while (parentID && !trailIds.has(parentID)) {
            trailIds.add(parentID);
            parentID = currentAttributes.find((attribute) => attribute.id === parentID)?.parentID;
        }
    };
    selectedIds.forEach(climb);
    leafOwnerIds.forEach((id) => {
        trailIds.add(id);
        climb(id);
    });
    // Scelta una sua riga, l'attributo non si accende: si accende la riga.
    const rowSelected = (node: ComposerAttributeNode) => selectedIds.includes(node.id) && !leafOwnerIds.has(node.id);

    const leafAttributeIds = filteredData.filter(hasLeaves).map((node) => node.id);
    const branchNodes = filteredData.filter((node) => node.type !== ComposerAttributeType.Attribute && getNodeChildren(node).length > 0);
    const anyBranchOpen = [...branchNodes.map((node) => node.id), ...leafAttributeIds].some((id) => openAttributeIds.has(id));
    const expandEverything = (expanded: boolean) => setOpenAttributeIds(new Set(expanded ? [...branchNodes.map((node) => node.id), ...leafAttributeIds] : []));

    // L'ALBERO ENTRA APERTO E RICORDA I RAMI CHIUSI (utente, 2026-10-16; `treeBranchMemory`), come Objects nello
    // Scene Editor: gruppi, passi e attributi aperti al primo accesso, e quello che chiudi resta chiuso al prossimo
    // accesso a questo prodotto. Prima si apriva solo il primo ramo (2026-10-15).
    const openableIds = React.useMemo(
        () =>
            currentAttributes
                .filter((node) => node.type === ComposerAttributeType.Attribute || currentAttributes.some((child) => child.parentID === node.id))
                .map((node) => node.id),
        [currentAttributes]
    );
    useRememberedBranches({
        scope: productState.product.id ? `composer:${productState.product.id}` : null,
        branchIds: openableIds,
        open: openAttributeIds,
        setOpen: setOpenAttributeIds
    });

    const renderNodeChildren = (node: ComposerAttributeNode, level: number): React.ReactNode[] => {
        const children = getNodeChildren(node).sort((left, right) => sortByDisplay(visibleData, left, right));
        return children.map((child) => {
            const descendants = child.type === ComposerAttributeType.Attribute ? [] : renderNodeChildren(child, level + 1);
            return (
                <ComposerAttributeTreeElement
                    key={child.id}
                    attribute={child}
                    type={child.type}
                    areaCount={areas.filter((area) => isComposerAreaLinkedToAttribute(area, child)).length}
                    hasLink={child.links.length > 0}
                    selected={rowSelected(child)}
                    trail={trailIds.has(child.id)}
                    shift={level}
                    expanded={rowExpanded(child)}
                    highlight={searchText}
                    onSelect={nodeSelectHandler}
                    onMoveDown={() => moveNodeHandler(child, 'down')}
                    onMoveUp={() => moveNodeHandler(child, 'up')}
                    onRowPointerDown={startStructureDrag}
                    onDoubleClick={() => {
                        selectSingleAttribute(child);
                        if (child.type === ComposerAttributeType.Attribute) toggleBranch(child);
                    }}
                    onAction={onAttributeRowAction}
                    selection={selectedIds.includes(child.id) ? rowSelection : undefined}
                    onVisibilityClick={toggleAttributeEnabled}
                    onRename={selectedIds.length === 1 ? renameAttribute : undefined}
                    onToggleExpand={() => toggleBranch(child)}
                >
                    {child.type === ComposerAttributeType.Attribute ? attributeLeaves(child, level) : descendants.length > 0 ? descendants : undefined}
                </ComposerAttributeTreeElement>
            );
        });
    };

    // SELECT ALL PRENDE LE RIGHE CHE SI VEDONO, come il clic con ⇧: ciò che sta in un ramo chiuso resta fuori.
    // Sta qui, dopo `rowExpanded`: calcolato prima, al primo disegno la funzione non esisteva ancora.
    const selectableRows = getVisibleStructureRows()
        .map((row) => currentAttributes.find((attribute) => attribute.id === row.id))
        .filter(Boolean) as ComposerAttributeNode[];
    // LA SELEZIONE HA UNA SPECIE SOLA (prototipo): scelta un'opzione o un'area, l'albero tiene acceso anche il suo
    // attributo, ma è la scia. Select all e il «…» della testata valgono per le righe di quella specie.
    const leafKind: 'option' | 'area' | 'structure' = selectedOptions?.length ? 'option' : selectedAreas?.length ? 'area' : 'structure';
    const visibleLeaves = leafKind === 'structure' ? [] : visibleTreeRows();
    const visibleOptions = visibleLeaves.flatMap((row) => (row.option ? [row.option] : []));
    const visibleAreas = visibleLeaves.flatMap((row) => (row.area ? [row.area] : []));
    const chosenIds = new Set(
        leafKind === 'option'
            ? (selectedOptions ?? []).map((option) => option.id)
            : leafKind === 'area'
              ? (selectedAreas ?? []).map((area) => area.id)
              : selectedIds
    );
    const selectableIds =
        leafKind === 'option'
            ? visibleOptions.map((option) => option.id)
            : leafKind === 'area'
              ? visibleAreas.map((area) => area.id)
              : selectableRows.map((row) => row.id);
    const allRowsSelected = selectableIds.length > 0 && selectableIds.every((id) => chosenIds.has(id));
    const selectedRows = selectedIds.length ? (storeSelectedAttributes ?? []) : [];
    const hasSelection = leafKind !== 'structure' || selectedRows.length > 0;
    const ownerOfOption = (option: ComposerAttributeOptionNode) => currentAttributes.find((attribute) => attribute.id === option.attributeID);
    const toggleSelectAll = () => {
        if (leafKind === 'option') {
            const next = allRowsSelected ? [] : visibleOptions;
            const owner = next.length ? ownerOfOption(next[0]) : undefined;
            if (owner) selectAttributes([owner]);
            return selectOptions(next);
        }
        if (leafKind === 'area') return selectAreas(allRowsSelected ? [] : visibleAreas);
        const next = allRowsSelected ? [] : selectableRows;
        setSelectedIds(next.map((row) => row.id));
        selectAttributes(next.length ? next : null);
        selectOptions(null);
        selectAreas(null);
    };
    const removeIcon = (
        <DropdownItemIcon>
            <RemoveIcon />
        </DropdownItemIcon>
    );
    const selectionMenuSections =
        leafKind === 'option'
            ? [
                  [
                      <DropdownActionItem key='duplicate' icon={<DuplicateIcon />} onClick={() => cloneOptions(selectedOptions ?? [])}>
                          {T._('Clone', 'Admin')}
                      </DropdownActionItem>,
                      <DropdownActionItem key='move' icon={<MoveToIcon />} onClick={() => openMoveOptions(selectionMenuRef.current, selectedOptions ?? [])}>
                          {T._('Move to…', 'Admin')}
                      </DropdownActionItem>,
                      <DropdownActionItem
                          key='copy'
                          icon={<CopyToIcon />}
                          onClick={() => openCopyPicker(selectionMenuRef.current, selectedOptions ?? [], 'copy')}
                      >
                          {T._('Copy to…', 'Admin')}
                      </DropdownActionItem>
                  ],
                  [
                      <ConfirmDropdownItem key='remove' icon={removeIcon} onConfirm={() => removeOptions(selectedOptions ?? [])}>
                          {T._('Remove', 'Admin')}
                      </ConfirmDropdownItem>
                  ]
              ]
            : leafKind === 'area'
              ? [
                    [
                        <DropdownActionItem key='duplicate' icon={<DuplicateIcon />} onClick={() => cloneAreas(selectedAreas ?? [])}>
                            {T._('Clone', 'Admin')}
                        </DropdownActionItem>
                    ],
                    [
                        <ConfirmDropdownItem key='remove' icon={removeIcon} onConfirm={() => removeAreas(selectedAreas ?? [])}>
                            {T._('Remove', 'Admin')}
                        </ConfirmDropdownItem>
                    ]
                ]
              : [
                    [
                        <DropdownActionItem
                            key='duplicate'
                            disabled={!selectionCommands.selectionRules.canDuplicate}
                            icon={<DuplicateIcon />}
                            onClick={selectionCommands.duplicateSelection}
                        >
                            {T._('Clone', 'Admin')}
                        </DropdownActionItem>,
                        <DropdownActionItem
                            key='move'
                            disabled={!canMoveOrCopy(selectedRows)}
                            icon={<MoveToIcon />}
                            onClick={() => openMoveTo(selectionMenuRef.current, selectedRows)}
                        >
                            {T._('Move to…', 'Admin')}
                        </DropdownActionItem>,
                        <DropdownActionItem
                            key='copy'
                            disabled={!canMoveOrCopy(selectedRows)}
                            icon={<CopyToIcon />}
                            onClick={() => openCopyTo(selectionMenuRef.current, selectedRows)}
                        >
                            {T._('Copy to…', 'Admin')}
                        </DropdownActionItem>,
                        <DropdownActionItem
                            key='group'
                            disabled={!selectionCommands.selectionRules.canGroup}
                            icon={<GroupIcon />}
                            onClick={selectionCommands.groupSelection}
                        >
                            {T._('Group', 'Admin')}
                        </DropdownActionItem>,
                        <DropdownActionItem
                            key='merge'
                            disabled={!selectionCommands.selectionRules.canMerge}
                            icon={<MergeIcon />}
                            onClick={selectionCommands.mergeSelection}
                        >
                            {T._('Merge', 'Admin')}
                        </DropdownActionItem>
                    ],
                    [
                        <ConfirmDropdownItem key='remove' icon={removeIcon} onConfirm={selectionCommands.removeSelection}>
                            {T._('Remove', 'Admin')}
                        </ConfirmDropdownItem>
                    ]
                ];

    return (
        <ComposerPanel aria-label={T._('Configurator structure', 'Admin')}>
            <ComposerToolbar aria-label={T._('Product commands', 'Admin')}>
                {addPicker}
                <ComposerAddButton data-composer-add onClick={(event) => openAddMenu(event.currentTarget)}>
                    <Plus />
                    {T._('Add', 'Admin')}
                </ComposerAddButton>
                {/* SCREENSHOT, MERGE E COPY NON STANNO PIÙ QUI (utente, 2026-10-15): valgono per una selezione, e stanno
                    nel pannello della selezione multipla e nel «…» delle righe scelte. */}
                {/* ESPANDI E COMPRIMI IN UN BOTTONE SOLO, come in Objects della scena (round 4, pacchetto F): con
                    un ramo aperto chiude tutto, altrimenti apre tutto. Prima erano due link su una riga a sé. */}
                <ComposerToolbarSpacer />
                {/* LA TESTATA È UGUALE IN OGNI ELENCO (prototipo, AGGIORNAMENTI §4; utente, 2026-10-20): Add a sinistra; a
                    destra Select all, un divisore, i comandi dell'elenco e in fondo il «…» della selezione. */}
                <ZTooltip title={allRowsSelected ? T._('Select none', 'Admin') : T._('Select all', 'Admin')} position='bottom' addContainer>
                    <ComposerIconButton
                        aria-label={allRowsSelected ? T._('Select none', 'Admin') : T._('Select all', 'Admin')}
                        aria-pressed={allRowsSelected}
                        $active={allRowsSelected}
                        disabled={!selectableIds.length}
                        onClick={toggleSelectAll}
                    >
                        <SelectAllIcon />
                    </ComposerIconButton>
                </ZTooltip>
                <ComposerToolbarDivider aria-hidden='true' />
                <ZTooltip
                    title={singleBranch ? T._('One branch at a time: opening a branch closes the others', 'Admin') : T._('One branch at a time', 'Admin')}
                    position='bottom'
                    addContainer
                >
                    <ComposerIconButton
                        aria-label={T._('One branch at a time', 'Admin')}
                        aria-pressed={singleBranch}
                        $active={singleBranch}
                        onClick={() => {
                            if (!singleBranch) expandEverything(false);
                            setSingleBranch(!singleBranch);
                        }}
                    >
                        <SingleBranchIcon />
                    </ComposerIconButton>
                </ZTooltip>
                <ZTooltip
                    title={
                        singleBranch && !anyBranchOpen
                            ? T._('One branch at a time is on: open a single branch from its arrow', 'Admin')
                            : anyBranchOpen
                              ? T._('Collapse all', 'Admin')
                              : T._('Expand all', 'Admin')
                    }
                    position='bottom'
                    addContainer
                >
                    <ComposerIconButton
                        aria-label={anyBranchOpen ? T._('Collapse all', 'Admin') : T._('Expand all', 'Admin')}
                        disabled={(!branchNodes.length && !leafAttributeIds.length) || (singleBranch && !anyBranchOpen)}
                        onClick={() => expandEverything(!anyBranchOpen)}
                    >
                        {anyBranchOpen ? <CollapseAllIcon /> : <ExpandAllIcon />}
                    </ComposerIconButton>
                </ZTooltip>
                {/* IL «…» DELLA SELEZIONE: spento finché non c'è niente di scelto, non compare e non scompare. */}
                {hasSelection ? (
                    <DropdownActions
                        header={
                            <ZTooltip title={T._('Selection commands', 'Admin')} position='bottom'>
                                <ComposerIconButton ref={selectionMenuRef} aria-label={T._('Selection commands', 'Admin')}>
                                    <MoreIcon />
                                </ComposerIconButton>
                            </ZTooltip>
                        }
                        sections={selectionMenuSections}
                        minWidth='220px'
                        orientation='RIGHT'
                    />
                ) : (
                    <ZTooltip title={T._('Select something in the list first', 'Admin')} position='bottom' addContainer>
                        <ComposerIconButton disabled aria-label={T._('Selection commands', 'Admin')}>
                            <MoreIcon />
                        </ComposerIconButton>
                    </ZTooltip>
                )}
            </ComposerToolbar>
            <SearchRow>
                <DropdownActions
                    header={
                        <ZTooltip title={T._('Filter the tree', 'Admin')} position='bottom' addContainer>
                            <FilterButton type='button' $active={filterActive} aria-label={T._('Filter the tree', 'Admin')}>
                                <FilterIcon />
                                {typeFilterLabels[typeFilter]}
                                {stateFilter.size > 0 && ` · ${stateFilter.size}`}
                            </FilterButton>
                        </ZTooltip>
                    }
                    sections={[
                        (Object.keys(typeFilterLabels) as StructureTypeFilter[]).map((key) => (
                            <DropdownActionItem key={key} selected={typeFilter === key} onClick={() => setTypeFilter(key)}>
                                {typeFilterLabels[key]}
                            </DropdownActionItem>
                        )),
                        [
                            <DropdownActionItem
                                key='hidden'
                                selected={stateFilter.has('hidden')}
                                caption={T._('Disabled rows', 'Admin')}
                                onClick={() => toggleStateFilter('hidden')}
                            >
                                {T._('Hidden', 'Admin')}
                            </DropdownActionItem>,
                            <DropdownActionItem
                                key='links'
                                selected={stateFilter.has('links')}
                                caption={T._('Shown only on a condition', 'Admin')}
                                onClick={() => toggleStateFilter('links')}
                            >
                                {T._('With links', 'Admin')}
                            </DropdownActionItem>
                        ]
                    ]}
                    minWidth='220px'
                />
                <SearchInput
                    aria-label={T._('Search structure', 'Admin')}
                    placeholder={T._('Search attributes and options…', 'Admin')}
                    value={filter}
                    onChange={(event) => setFilter(event.target.value)}
                    onClear={() => setFilter('')}
                />
            </SearchRow>
            <TreeDropSurface
                ref={treeKeyboard.treeRef}
                onFocus={treeKeyboard.onFocus}
                onKeyDown={treeKeyboard.onKeyDown}
                aria-label={T._('Product structure', 'Admin')}
                data-reorder-scope=''
                onClick={(event) => {
                    // UN CLIC NEL VUOTO TOGLIE LA SELEZIONE (utente, 2026-10-15): solo sul fondo dell'albero,
                    // non su una riga, un menu o un bottone.
                    const target = event.target as HTMLElement;
                    if (target !== event.currentTarget && !target.hasAttribute('data-tree-background')) return;
                    clearSelection();
                }}
                role='tree'
            >
                {nodes.length === 0 ? (
                    <ComposerEmptyState icon={<AttributeIcon />}>
                        {filter.trim() ? (
                            stringFormat(T._('Nothing matches “{0}”.', 'Admin'), filter.trim())
                        ) : filterActive ? (
                            T._('Nothing to show with this filter.', 'Admin')
                        ) : (
                            // L'ADD STA DENTRO LA FRASE (prototipo): l'elenco vuoto dice da dove si comincia e lo fa fare.
                            <>
                                {T._('Nothing here yet —', 'Admin')}{' '}
                                <EmptyAddButton type='button' onClick={(event) => openAddMenu(event.currentTarget)}>
                                    {T._('Add', 'Admin')}
                                </EmptyAddButton>{' '}
                                {T._('starts one.', 'Admin')}
                            </>
                        )}
                    </ComposerEmptyState>
                ) : (
                    <TreeList data-tree-background=''>
                        {nodes.map((node) => {
                            const children = getNodeChildren(node);
                            const sortedChildren = children.sort((a, b) => sortByDisplay(children, a, b));

                            return (
                                <ComposerAttributeTreeElement
                                    key={node.id}
                                    attribute={node}
                                    selected={rowSelected(node)}
                                    trail={trailIds.has(node.id)}
                                    type={node.type}
                                    areaCount={areas.filter((area) => isComposerAreaLinkedToAttribute(area, node)).length}
                                    hasLink={node.links && node.links.length > 0}
                                    onSelect={nodeSelectHandler}
                                    onRowPointerDown={startStructureDrag}
                                    expanded={rowExpanded(node)}
                                    highlight={searchText}
                                    onMoveDown={(node) => {
                                        moveNodeHandler(node, 'down');
                                    }}
                                    onMoveUp={(node) => {
                                        moveNodeHandler(node, 'up');
                                    }}
                                    onDoubleClick={() => {
                                        selectSingleAttribute(node);
                                        if (node.type === ComposerAttributeType.Attribute) toggleBranch(node);
                                    }}
                                    onAction={onAttributeRowAction}
                                    selection={selectedIds.includes(node.id) ? rowSelection : undefined}
                                    onVisibilityClick={toggleAttributeEnabled}
                                    onRename={selectedIds.length === 1 ? renameAttribute : undefined}
                                    onToggleExpand={toggleBranch}
                                >
                                    {node.type === ComposerAttributeType.Attribute
                                        ? attributeLeaves(node, 0)
                                        : sortedChildren.length > 0
                                          ? renderNodeChildren(node, 1)
                                          : undefined}
                                </ComposerAttributeTreeElement>
                            );
                        })}
                    </TreeList>
                )}
            </TreeDropSurface>
            {/* Mentre si cerca o si filtra i gesti della selezione non servono: la nota si toglie (prototipo). */}
            {nodes.length > 1 && !searchText && !filterActive && (
                <ComposerSelectionFooter>
                    {keyText(T._('⇧click selects a range · ⌘click adds or removes one · ⋮ on a selected row acts on all of them', 'Admin'))}
                </ComposerSelectionFooter>
            )}
            {imagePicker}
            {structurePicker}
            {optionMovePicker}
        </ComposerPanel>
    );
};

export default ComposerAttributes;
